<script setup lang="ts">
import axios from 'axios'
import { VDateInput } from 'vuetify/labs/VDateInput'
import { notifyError } from '@/utils/helpers'
import type { ECOption } from '@/plugins/echarts'

definePage({
    meta: {
        id: 'capteurs.meteo',
        title: 'Station météo',
        icon: 'mdi-weather-partly-cloudy',
        drawerIndex: 9,
        requiresAuth: true,
        requiresSiteAccess: 'agri.capteurs',
    },
})

const TB_BASE_URL = import.meta.env.VITE_TB_URL
const theme = useTheme()
const { smAndDown } = useDisplay()
const isLoading = ref(false)

// ── ThingsBoard auth ──────────────────────────────────────────────────────────

const tbToken = ref<string | null>(null)
const tbAuthority = ref<string | null>(null)
const loginError = ref('')

async function getTbToken(): Promise<string | null> {
    if (tbToken.value) return tbToken.value
    try {
        const response = await apiOmni.post('api/tb/login')
        tbToken.value = response.data.token
        void loadTbAuthority()
        return tbToken.value
    } catch (e: unknown) {
        const err = e as { response?: { status: number; data: unknown }; message?: string }
        loginError.value = err.response
            ? `HTTP ${err.response.status}: ${JSON.stringify(err.response.data)}`
            : (err.message ?? String(e))
        notifyError(loginError.value)
        return null
    }
}

function tbHeaders(token: string): Record<string, string> {
    return {
        'X-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    }
}

async function loadTbAuthority() {
    if (!tbToken.value || tbAuthority.value) return
    try {
        const resp = await axios.get(`${TB_BASE_URL}/api/auth/user`, { headers: tbHeaders(tbToken.value) })
        tbAuthority.value = resp.data?.authority ?? null
    } catch {
        tbAuthority.value = null
    }
}

const peutRenommer = computed(() => tbAuthority.value === 'TENANT_ADMIN')

// ── Types ─────────────────────────────────────────────────────────────────────

declare interface TbDeviceId {
    id: string
    entityType: string
}

declare interface TbDevice {
    id: TbDeviceId
    name: string
    type: string
    label: string
    createdTime: number
}

declare interface TbPageData<T> {
    data: T[]
    hasNext: boolean
}

declare interface ChartPoint { ts: number; value: number }

declare interface JourPluie { jour: string; ts: number; mm: number }

declare interface S2120Latest {
    temp: number | null
    hum: number | null
    ventMs: number | null
    rafaleMs: number | null
    dirDeg: number | null
    précipCumul: number | null
    tauxPrécip: number | null
    pression: number | null
    lux: number | null
    uv: number | null
    batteriePct: number | null
    rssi: number | null
    snr: number | null
    ts: number | null
}

// ── State ─────────────────────────────────────────────────────────────────────

const DEVICE_TYPE = 'S2120'

const devices = ref<TbDevice[]>([])
const selectedDevice = ref<TbDevice | null>(null)
const activeId = ref<string | null>(null)

const latestVide = (): S2120Latest => ({
    temp: null, hum: null, ventMs: null, rafaleMs: null, dirDeg: null,
    précipCumul: null, tauxPrécip: null, pression: null, lux: null, uv: null,
    batteriePct: null, rssi: null, snr: null, ts: null,
})

const latest = ref<S2120Latest>(latestVide())
const isLoadingTelemetry = ref(false)

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDuration(ms: number): string {
    const minutes = Math.floor(ms / 60000)
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} h ${minutes % 60} min`
    const days = Math.floor(hours / 24)
    return `${days} j ${hours % 24} h`
}

const tempsDepuisDernière = computed(() =>
    latest.value.ts ? formatDuration(Date.now() - latest.value.ts) : null,
)

function isoJour(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function débutJourLocal(d: Date): number {
    const x = new Date(d)
    x.setHours(0, 0, 0, 0)
    return x.getTime()
}

const CARDINAUX = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSO', 'SO', 'OSO', 'O', 'ONO', 'NO', 'NNO']

function cardinal(deg: number): string {
    return CARDINAUX[Math.round(deg / 22.5) % 16]
}

function msVersKmh(ms: number | null): number | null {
    return ms === null ? null : ms * 3.6
}

// Saison agricole : du 1er avril à la fin mars suivante.
function débutSaisonTs(): number {
    const now = new Date()
    const année = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1
    return new Date(année, 3, 1).getTime()
}

// ── Latest telemetry ──────────────────────────────────────────────────────────

const CLÉS_LATEST = [
    'data_Temp_c', 'data_Hum', 'data_Current_wind_dir', 'data_Current_wind_speed',
    'data_Precip_cumul', 'data_Precip_rate', 'data_Pressure', 'data_Wind_gust_3s',
    'data_Light_lux', 'data_Uv_index', 'data_battery_pct', 'rssi', 'snr',
].join(',')

async function loadTelemetry(deviceId: string) {
    isLoadingTelemetry.value = true
    latest.value = latestVide()
    try {
        const token = await getTbToken()
        if (!token) return
        const url =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries?keys=${CLÉS_LATEST}`
        const resp = await axios.get(url, { headers: tbHeaders(token) })
        const d = resp.data
        const num = (clé: string): number | null =>
            d?.[clé]?.[0]?.value !== undefined ? parseFloat(d[clé][0].value) : null
        latest.value = {
            temp: num('data_Temp_c'),
            hum: num('data_Hum'),
            ventMs: num('data_Current_wind_speed'),
            rafaleMs: num('data_Wind_gust_3s'),
            dirDeg: num('data_Current_wind_dir'),
            précipCumul: num('data_Precip_cumul'),
            tauxPrécip: num('data_Precip_rate'),
            pression: num('data_Pressure'),
            lux: num('data_Light_lux'),
            uv: num('data_Uv_index'),
            batteriePct: num('data_battery_pct'),
            rssi: num('rssi'),
            snr: num('snr'),
            ts: d?.data_Temp_c?.[0]?.ts ?? d?.data_Precip_cumul?.[0]?.ts ?? null,
        }
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingTelemetry.value = false
    }
}

// ── Condition courante (héro façon Apple Météo) ───────────────────────────────

const condition = computed<'pluie' | 'soleil' | 'nuageux' | 'nuit'>(() => {
    const l = latest.value
    if ((l.tauxPrécip ?? 0) > 0) return 'pluie'
    if ((l.lux ?? 0) > 30000) return 'soleil'
    if ((l.lux ?? 0) > 100) return 'nuageux'
    return 'nuit'
})

const CONDITIONS = {
    pluie: { icône: 'mdi-weather-pouring', texte: 'Pluie en cours', dégradé: 'linear-gradient(160deg, #3E5570 0%, #64748B 100%)' },
    soleil: { icône: 'mdi-weather-sunny', texte: 'Ensoleillé', dégradé: 'linear-gradient(160deg, #1D6FD8 0%, #5AA7F0 100%)' },
    nuageux: { icône: 'mdi-weather-partly-cloudy', texte: 'Variable', dégradé: 'linear-gradient(160deg, #4A6D96 0%, #86A6C6 100%)' },
    nuit: { icône: 'mdi-weather-night', texte: 'Nuit', dégradé: 'linear-gradient(160deg, #17233B 0%, #3A4C6B 100%)' },
} as const

const héroCondition = computed(() => CONDITIONS[condition.value])

// ── Précipitations journalières ───────────────────────────────────────────────

// L'agrégation ThingsBoard expire au-delà de ~150 jours : on découpe la période en tranches.
const TRANCHE_MS = 120 * 86400000

async function fetchMaxJournalier(deviceId: string, startTs: number, endTs: number): Promise<ChartPoint[]> {
    const token = await getTbToken()
    if (!token) return []
    const points: ChartPoint[] = []
    for (let s = startTs; s < endTs; s += TRANCHE_MS) {
        const e = Math.min(s + TRANCHE_MS, endTs)
        const url =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
            `?keys=data_Precip_cumul&startTs=${s}&endTs=${e}&interval=86400000&agg=MAX&limit=50000`
        const resp = await axios.get(url, { headers: tbHeaders(token) })
        for (const p of resp.data?.data_Precip_cumul ?? [])
            points.push({ ts: p.ts, value: parseFloat(p.value) })
    }
    return points.sort((a, b) => a.ts - b.ts)
}

const pluieParJour = ref<JourPluie[]>([])
const pluieChargéeDepuis = ref<number | null>(null)
const isLoadingPluie = ref(false)
// Complétion de la saison en arrière-plan : ne masque pas le graphique déjà affiché.
const isLoadingSaison = ref(false)

async function loadPluie(deviceId: string, depuisTs: number, enArrièrePlan = false) {
    if (enArrièrePlan) isLoadingSaison.value = true
    else isLoadingPluie.value = true
    try {
        // Un jour de plus en amont pour disposer d'une valeur de référence du compteur.
        const startTs = débutJourLocal(new Date(depuisTs)) - 86400000
        const points = await fetchMaxJournalier(deviceId, startTs, Date.now())
        // L'appareil a pu changer pendant la requête : ne pas écraser les données de l'autre station.
        if (selectedDevice.value?.id.id !== deviceId) return
        const jours: JourPluie[] = []
        for (let i = 1; i < points.length; i++) {
            const précédent = points[i - 1].value
            const courant = points[i].value
            // Compteur cumulatif : un recul signale un redémarrage de la station.
            const mm = courant >= précédent ? courant - précédent : courant
            const jourTs = débutJourLocal(new Date(points[i].ts))
            jours.push({ jour: isoJour(new Date(jourTs)), ts: jourTs, mm: Math.round(mm * 100) / 100 })
        }
        pluieParJour.value = jours
        pluieChargéeDepuis.value = startTs + 86400000
    } catch (e) {
        notifyError(e)
    } finally {
        if (enArrièrePlan) isLoadingSaison.value = false
        else isLoadingPluie.value = false
    }
}

// 7 jours d'abord pour un premier affichage rapide, puis la saison complète en arrière-plan.
async function loadPluieInitiale(deviceId: string) {
    await loadPluie(deviceId, Date.now() - 6 * 86400000)
    if (selectedDevice.value?.id.id !== deviceId) return
    const débutSaison = débutSaisonTs()
    if (pluieChargéeDepuis.value === null || débutSaison < pluieChargéeDepuis.value)
        await loadPluie(deviceId, débutSaison, true)
}

// ── Statistiques de pluie ─────────────────────────────────────────────────────

function sommePluieDepuis(jourMin: string): number | null {
    if (pluieParJour.value.length === 0) return null
    // Données pas encore chargées aussi loin en arrière : mieux vaut un « … » qu'un cumul tronqué.
    if (pluieChargéeDepuis.value !== null && new Date(`${jourMin}T00:00:00`).getTime() < pluieChargéeDepuis.value)
        return null
    return Math.round(
        pluieParJour.value.filter(j => j.jour >= jourMin).reduce((acc, j) => acc + j.mm, 0) * 100,
    ) / 100
}

const jourAujourdhui = computed(() => isoJour(new Date()))
const jourHier = computed(() => isoJour(new Date(Date.now() - 86400000)))

const pluieAujourdhui = computed(() => {
    const j = pluieParJour.value.find(x => x.jour === jourAujourdhui.value)
    return j ? j.mm : (pluieParJour.value.length ? 0 : null)
})
const pluieHier = computed(() => {
    const j = pluieParJour.value.find(x => x.jour === jourHier.value)
    return j ? j.mm : (pluieParJour.value.length ? 0 : null)
})
const pluie7j = computed(() => sommePluieDepuis(isoJour(new Date(Date.now() - 6 * 86400000))))
const pluie30j = computed(() => sommePluieDepuis(isoJour(new Date(Date.now() - 29 * 86400000))))
const pluieSaison = computed(() => sommePluieDepuis(isoJour(new Date(débutSaisonTs()))))

declare interface TuilePluie { label: string; valeur: number | null; icône: string }

const tuilesPluie = computed<TuilePluie[]>(() => [
    { label: 'Aujourd\'hui', valeur: pluieAujourdhui.value, icône: 'mdi-calendar-today' },
    { label: 'Hier', valeur: pluieHier.value, icône: 'mdi-history' },
    { label: '7 derniers jours', valeur: pluie7j.value, icône: 'mdi-calendar-week' },
    { label: '30 derniers jours', valeur: pluie30j.value, icône: 'mdi-calendar-month' },
    { label: 'Saison (1ᵉʳ avril)', valeur: pluieSaison.value, icône: 'mdi-sprout' },
])

// ── Période du graphique de précipitations ────────────────────────────────────

type PériodePluie = '7j' | '30j' | 'saison' | 'custom'

const périodePluie = ref<PériodePluie>('7j')
const startDate = ref(isoJour(new Date(Date.now() - 6 * 86400000)))
const endDate = ref(isoJour(new Date()))

function plageDepuisBornes(min: string, max: string): Date[] {
    if (!min || !max) return []
    const dates: Date[] = []
    const jour = new Date(`${min}T00:00:00`)
    const fin = new Date(`${max}T00:00:00`)
    while (jour <= fin) {
        dates.push(new Date(jour))
        jour.setDate(jour.getDate() + 1)
    }
    return dates
}

const plagePluie = computed<Date[]>({
    get: () => plageDepuisBornes(startDate.value, endDate.value),
    set: (dates) => {
        if (dates.length === 0) return
        startDate.value = isoJour(dates[0])
        endDate.value = isoJour(dates[dates.length - 1])
        périodePluie.value = 'custom'
        void assurerPluieChargée(new Date(`${startDate.value}T00:00:00`).getTime())
    },
})

function appliquerPériodePluie(mode: PériodePluie) {
    périodePluie.value = mode
    const fin = new Date()
    endDate.value = isoJour(fin)
    if (mode === '7j') startDate.value = isoJour(new Date(Date.now() - 6 * 86400000))
    else if (mode === '30j') startDate.value = isoJour(new Date(Date.now() - 29 * 86400000))
    else if (mode === 'saison') startDate.value = isoJour(new Date(débutSaisonTs()))
    void assurerPluieChargée(new Date(`${startDate.value}T00:00:00`).getTime())
}

async function assurerPluieChargée(depuisTs: number) {
    if (!selectedDevice.value) return
    if (pluieChargéeDepuis.value !== null && depuisTs >= pluieChargéeDepuis.value) return
    await loadPluie(selectedDevice.value.id.id, depuisTs)
}

function rafraîchirPluie() {
    if (!selectedDevice.value) return
    const départ = Math.min(débutSaisonTs(), new Date(`${startDate.value}T00:00:00`).getTime())
    void loadPluie(selectedDevice.value.id.id, départ)
}

const pluieAffichée = computed<JourPluie[]>(() =>
    pluieParJour.value.filter(j => j.jour >= startDate.value && j.jour <= endDate.value),
)

// ── Couleurs des graphiques ───────────────────────────────────────────────────

const COULEUR_PLUIE = '#3B82F6'
const COULEUR_CUMUL = '#F59E0B'

const estSombre = computed(() => theme.global.name.value === 'dark')
const couleurTexte = computed(() => (estSombre.value ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)'))
const couleurGrille = computed(() => (estSombre.value ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'))

function dégradé(couleur: string, opacité: number) {
    const alpha = Math.round(opacité * 255).toString(16).padStart(2, '0')
    return {
        type: 'linear' as const,
        x: 0, y: 0, x2: 0, y2: 1,
        colorStops: [
            { offset: 0, color: `${couleur}${alpha}` },
            { offset: 1, color: `${couleur}00` },
        ],
    }
}

// ── Graphique précipitations (barres journalières + cumul) ────────────────────

const pluieChartOption = computed<ECOption>(() => {
    const jours = pluieAffichée.value
    if (jours.length === 0) return {}
    let cumul = 0
    const cumuls = jours.map(j => {
        cumul += j.mm
        return Math.round(cumul * 100) / 100
    })
    const axeCommun = {
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: couleurTexte.value, fontSize: 10 },
        nameTextStyle: { color: couleurTexte.value, fontSize: 10 },
    }
    return {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            backgroundColor: estSombre.value ? 'rgba(30,30,30,0.94)' : 'rgba(255,255,255,0.96)',
            borderWidth: 0,
            padding: [8, 12],
            textStyle: { color: estSombre.value ? '#fff' : '#000', fontSize: 12 },
            formatter(params: unknown) {
                const items = params as Array<{ seriesName: string; value: number; marker: string; name: string }>
                if (!items.length) return ''
                const date = new Date(`${items[0].name}T00:00:00`)
                    .toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' })
                let html = `<div style="opacity:.7;margin-bottom:4px;">${date}</div>`
                for (const p of items) {
                    const unit = ' mm'
                    html += `${p.marker}${p.seriesName} <b style="float:right;margin-left:16px;">${p.value.toFixed(1)}${unit}</b><br/>`
                }
                return html
            },
        },
        legend: {
            top: 0,
            right: 8,
            icon: 'roundRect',
            itemWidth: 14,
            itemHeight: 4,
            textStyle: { color: couleurTexte.value, fontSize: 11 },
        },
        grid: { top: 34, left: 8, right: 8, bottom: 4, containLabel: true },
        xAxis: {
            type: 'category',
            data: jours.map(j => j.jour),
            ...axeCommun,
            axisLabel: {
                color: couleurTexte.value,
                fontSize: 10,
                formatter: (v: string) =>
                    new Date(`${v}T00:00:00`).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' }),
            },
        },
        yAxis: [
            {
                type: 'value',
                name: 'mm / jour',
                position: 'left',
                ...axeCommun,
                splitLine: { show: true, lineStyle: { color: couleurGrille.value } },
            },
            {
                type: 'value',
                name: 'cumul (mm)',
                position: 'right',
                ...axeCommun,
                splitLine: { show: false },
            },
        ],
        dataZoom: [{ type: 'inside', zoomOnMouseWheel: 'shift', moveOnMouseWheel: false }],
        series: [
            {
                name: 'Pluie',
                type: 'bar',
                yAxisIndex: 0,
                data: jours.map(j => j.mm),
                barMaxWidth: 26,
                itemStyle: {
                    borderRadius: [4, 4, 0, 0],
                    color: {
                        type: 'linear' as const,
                        x: 0, y: 0, x2: 0, y2: 1,
                        colorStops: [
                            { offset: 0, color: COULEUR_PLUIE },
                            { offset: 1, color: `${COULEUR_PLUIE}88` },
                        ],
                    },
                },
            },
            {
                name: 'Cumul',
                type: 'line',
                yAxisIndex: 1,
                data: cumuls,
                showSymbol: false,
                smooth: 0.2,
                lineStyle: { width: 2, color: COULEUR_CUMUL },
                itemStyle: { color: COULEUR_CUMUL },
            },
        ],
    }
})

// ── Tuiles de mesures + graphique au clic ─────────────────────────────────────

type MétriqueId = 'temp' | 'hum' | 'vent' | 'rafale' | 'pression' | 'lumiere' | 'uv' | 'batterie'

declare interface Métrique {
    id: MétriqueId
    clé: string
    label: string
    unité: string
    icône: string
    couleur: string
    décimales: number
    facteur: number
}

const MÉTRIQUES: Métrique[] = [
    { id: 'temp', clé: 'data_Temp_c', label: 'Température', unité: '°C', icône: 'mdi-thermometer', couleur: '#F97316', décimales: 1, facteur: 1 },
    { id: 'hum', clé: 'data_Hum', label: 'Humidité', unité: '%', icône: 'mdi-water-percent', couleur: '#38BDF8', décimales: 0, facteur: 1 },
    { id: 'vent', clé: 'data_Current_wind_speed', label: 'Vent', unité: 'km/h', icône: 'mdi-weather-windy', couleur: '#10B981', décimales: 1, facteur: 3.6 },
    { id: 'rafale', clé: 'data_Wind_gust_3s', label: 'Rafales', unité: 'km/h', icône: 'mdi-weather-dust', couleur: '#14B8A6', décimales: 1, facteur: 3.6 },
    { id: 'pression', clé: 'data_Pressure', label: 'Pression', unité: 'hPa', icône: 'mdi-gauge', couleur: '#8B5CF6', décimales: 1, facteur: 1 },
    { id: 'lumiere', clé: 'data_Light_lux', label: 'Luminosité', unité: 'lux', icône: 'mdi-white-balance-sunny', couleur: '#FBBF24', décimales: 0, facteur: 1 },
    { id: 'uv', clé: 'data_Uv_index', label: 'Indice UV', unité: '', icône: 'mdi-sunglasses', couleur: '#E879F9', décimales: 1, facteur: 1 },
    { id: 'batterie', clé: 'data_battery_pct', label: 'Batterie', unité: '%', icône: 'mdi-battery-high', couleur: '#4CAF50', décimales: 0, facteur: 1 },
]

function valeurMétrique(id: MétriqueId): number | null {
    const l = latest.value
    switch (id) {
        case 'temp': return l.temp
        case 'hum': return l.hum
        case 'vent': return msVersKmh(l.ventMs)
        case 'rafale': return msVersKmh(l.rafaleMs)
        case 'pression': return l.pression
        case 'lumiere': return l.lux
        case 'uv': return l.uv
        case 'batterie': return l.batteriePct
    }
}

const métriqueSélectionnée = ref<MétriqueId | null>(null)
const métriquePériodeJours = ref<1 | 7 | 30>(7)
const métriquePoints = ref<ChartPoint[]>([])
const isLoadingMétrique = ref(false)

const métriqueMeta = computed<Métrique | null>(() =>
    MÉTRIQUES.find(m => m.id === métriqueSélectionnée.value) ?? null,
)

function surClicTuile(id: MétriqueId) {
    if (métriqueSélectionnée.value === id) {
        métriqueSélectionnée.value = null
        return
    }
    métriqueSélectionnée.value = id
    void loadMétrique()
}

function appliquerPériodeMétrique(jours: 1 | 7 | 30) {
    métriquePériodeJours.value = jours
    void loadMétrique()
}

async function loadMétrique() {
    const device = selectedDevice.value
    const meta = métriqueMeta.value
    if (!device || !meta) return
    isLoadingMétrique.value = true
    métriquePoints.value = []
    try {
        const token = await getTbToken()
        if (!token) return
        const endTs = Date.now()
        const startTs = endTs - métriquePériodeJours.value * 86400000
        const url =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${device.id.id}/values/timeseries` +
            `?keys=${meta.clé}&startTs=${startTs}&endTs=${endTs}&limit=50000&orderBy=ASC`
        const resp = await axios.get(url, { headers: tbHeaders(token) })
        métriquePoints.value = (resp.data?.[meta.clé] ?? []).map((p: { ts: number; value: string }) => ({
            ts: p.ts,
            value: parseFloat(p.value) * meta.facteur,
        }))
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingMétrique.value = false
    }
}

const métriqueChartOption = computed<ECOption>(() => {
    const meta = métriqueMeta.value
    if (!meta || métriquePoints.value.length === 0) return {}
    const axeCommun = {
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: couleurTexte.value, fontSize: 10 },
        nameTextStyle: { color: couleurTexte.value, fontSize: 10 },
    }
    return {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'line', lineStyle: { color: couleurTexte.value, type: 'dashed' } },
            backgroundColor: estSombre.value ? 'rgba(30,30,30,0.94)' : 'rgba(255,255,255,0.96)',
            borderWidth: 0,
            padding: [8, 12],
            textStyle: { color: estSombre.value ? '#fff' : '#000', fontSize: 12 },
            formatter(params: unknown) {
                const items = params as Array<{ value: [number, number]; marker: string }>
                if (!items.length) return ''
                const date = new Date(items[0].value[0])
                    .toLocaleString('fr-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                return `<div style="opacity:.7;margin-bottom:4px;">${date}</div>` +
                    `${items[0].marker}${meta.label} <b style="float:right;margin-left:16px;">` +
                    `${items[0].value[1].toFixed(meta.décimales)} ${meta.unité}</b>`
            },
        },
        grid: { top: 24, left: 8, right: 8, bottom: 4, containLabel: true },
        xAxis: {
            type: 'time',
            ...axeCommun,
            splitLine: { show: true, lineStyle: { color: couleurGrille.value, type: 'dashed' } },
        },
        yAxis: {
            type: 'value',
            name: meta.unité,
            scale: true,
            ...axeCommun,
            splitLine: { show: true, lineStyle: { color: couleurGrille.value } },
        },
        dataZoom: [{ type: 'inside', zoomOnMouseWheel: 'shift', moveOnMouseWheel: false }],
        series: [{
            name: meta.label,
            type: 'line',
            showSymbol: false,
            smooth: 0.2,
            data: métriquePoints.value.map(p => [p.ts, p.value]),
            lineStyle: { width: 2, color: meta.couleur },
            itemStyle: { color: meta.couleur },
            areaStyle: { color: dégradé(meta.couleur, 0.28) },
        }],
    }
})

// ── Sélection et chargement ───────────────────────────────────────────────────

function onSelectDevice(device: TbDevice) {
    activeId.value = device.id.id
    selectedDevice.value = device
    latest.value = latestVide()
    pluieParJour.value = []
    pluieChargéeDepuis.value = null
    métriqueSélectionnée.value = null
    métriquePoints.value = []
    void loadTelemetry(device.id.id)
    void loadPluieInitiale(device.id.id)
}

async function fetchAllDevices(token: string): Promise<TbDevice[]> {
    const PAGE_SIZE = 100
    let page = 0
    const result: TbDevice[] = []
    while (true) {
        const url =
            `${TB_BASE_URL}/api/tenant/devices` +
            `?pageSize=${PAGE_SIZE}&page=${page}&type=${encodeURIComponent(DEVICE_TYPE)}`
        const response = await axios.get<TbPageData<TbDevice>>(url, { headers: tbHeaders(token) })
        result.push(...response.data.data)
        if (!response.data.hasNext) break
        page++
    }
    return result.sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name))
}

async function loadDevices() {
    loginError.value = ''
    isLoading.value = true
    try {
        const token = await getTbToken()
        if (!token) return
        devices.value = await fetchAllDevices(token)
        const courant = devices.value.find(d => d.id.id === activeId.value)
        if (courant) {
            onSelectDevice(courant)
        } else if (devices.value.length > 0) {
            onSelectDevice(devices.value[0])
        }
    } catch (e) {
        notifyError(e)
    } finally {
        isLoading.value = false
    }
}

onMounted(loadDevices)

// ── Renommer une station ──────────────────────────────────────────────────────

const renameDialog = ref(false)
const renameLabel = ref('')
const isSavingLabel = ref(false)

function openRenameDialog() {
    if (!selectedDevice.value) return
    renameLabel.value = selectedDevice.value.label || ''
    renameDialog.value = true
}

async function saveLabel() {
    const device = selectedDevice.value
    if (!device) return
    const label = renameLabel.value.trim()
    isSavingLabel.value = true
    try {
        const token = await getTbToken()
        if (!token) return
        // ThingsBoard remplace l'entité complète : on repart de la version courante du serveur.
        const current = await axios.get<TbDevice>(
            `${TB_BASE_URL}/api/device/${device.id.id}`,
            { headers: tbHeaders(token) },
        )
        const resp = await axios.post<TbDevice>(
            `${TB_BASE_URL}/api/device`,
            { ...current.data, label },
            { headers: tbHeaders(token) },
        )
        device.label = resp.data.label
        renameDialog.value = false
        Notify.success('Nom de la station modifié avec succès')
    } catch (e) {
        notifyError(e)
    } finally {
        isSavingLabel.value = false
    }
}
</script>

<template>
    <v-card class="page-card" :loading="isLoading">

        <!-- Page header -->
        <!-- Sur mobile il n'y a pas de v-app-bar : on décale à droite pour libérer le hamburger flottant. -->
        <div class="page-header" :class="smAndDown ? 'ps-16 pe-3 py-2' : 'px-5 pt-4 pb-3'">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
                <div class="d-flex align-center" style="gap: 12px; min-width: 0;">
                    <v-avatar v-if="!smAndDown" color="primary" variant="tonal" size="44" rounded="lg">
                        <v-icon icon="mdi-weather-partly-cloudy" size="26" />
                    </v-avatar>
                    <div>
                        <div :class="smAndDown ? 'text-subtitle-1' : 'text-h6'" class="font-weight-bold" style="line-height: 1.2;">
                            Station météo
                        </div>
                        <div v-if="!smAndDown" class="text-caption text-medium-emphasis">Stations SenseCAP S2120</div>
                    </div>
                </div>
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-btn
                        v-if="smAndDown"
                        icon="mdi-refresh"
                        color="primary"
                        variant="tonal"
                        size="small"
                        :loading="isLoading"
                        @click="loadDevices"
                    />
                    <v-btn
                        v-else
                        color="primary" variant="tonal" rounded="pill" prepend-icon="mdi-refresh"
                        :loading="isLoading" @click="loadDevices"
                    >
                        Rafraîchir
                    </v-btn>
                </div>
            </div>

            <!-- Sélecteur compact : une puce par station -->
            <div v-if="devices.length > 1" class="chips-stations mt-3">
                <v-chip
                    v-for="d in devices"
                    :key="d.id.id"
                    :color="activeId === d.id.id ? 'primary' : undefined"
                    :variant="activeId === d.id.id ? 'flat' : 'tonal'"
                    size="small"
                    prepend-icon="mdi-weather-partly-cloudy"
                    style="cursor: pointer; font-weight: 600;"
                    @click="onSelectDevice(d)"
                >
                    {{ d.label || d.name }}
                </v-chip>
            </div>
        </div>

        <v-divider />

        <!-- Error banner -->
        <v-alert
            v-if="loginError"
            type="error"
            variant="tonal"
            density="compact"
            class="mx-4 mt-3 mb-0 flex-shrink-0"
            rounded="lg"
        >
            {{ loginError }}
        </v-alert>

        <!-- Contenu défilable -->
        <div class="detail-scroll">

            <!-- Aucune station -->
            <div
                v-if="!isLoading && devices.length === 0 && !loginError"
                class="d-flex flex-column align-center justify-center text-medium-emphasis pa-4"
                style="height: 100%; gap: 12px;"
            >
                <v-icon icon="mdi-weather-partly-cloudy" size="48" />
                <span class="text-body-2 text-center">
                    Contactez-nous pour avoir une station météo sur votre ferme !
                    <a href="https://www.agrimetrie.com" target="_blank" rel="noopener noreferrer">
                        Cliquez ici pour plus d'informations.
                    </a>
                </span>
            </div>

            <template v-else-if="selectedDevice">

                <!-- ── Héro : conditions actuelles ──────────────────────────── -->
                <v-card class="hero-card" rounded="xl" flat :style="{ background: héroCondition.dégradé }">
                    <v-card-text :class="smAndDown ? 'pa-4' : 'pa-5'">
                        <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                            <div class="text-subtitle-1 font-weight-bold text-truncate" style="min-width: 0;">
                                {{ selectedDevice.label || selectedDevice.name }}
                            </div>
                            <v-btn
                                v-if="peutRenommer"
                                icon="mdi-pencil-outline"
                                variant="text"
                                size="x-small"
                                class="btn-hero"
                                @click="openRenameDialog"
                            />
                            <v-spacer />
                            <div v-if="!isLoadingTelemetry" class="d-flex align-center flex-wrap" style="gap: 6px;">
                                <v-chip
                                    v-if="latest.batteriePct !== null"
                                    size="x-small"
                                    class="chip-hero"
                                    :prepend-icon="latest.batteriePct >= 60 ? 'mdi-battery-high' : latest.batteriePct >= 30 ? 'mdi-battery-medium' : 'mdi-battery-low'"
                                >
                                    {{ latest.batteriePct.toFixed(0) }} %
                                </v-chip>
                                <v-chip
                                    v-if="latest.rssi !== null"
                                    size="x-small"
                                    class="chip-hero"
                                    :prepend-icon="latest.rssi >= -70 ? 'mdi-signal-cellular-3' : latest.rssi >= -85 ? 'mdi-signal-cellular-2' : 'mdi-signal-cellular-1'"
                                >
                                    {{ latest.rssi }} dBm
                                </v-chip>
                                <v-chip
                                    v-if="latest.snr !== null"
                                    size="x-small"
                                    class="chip-hero"
                                    prepend-icon="mdi-sine-wave"
                                >
                                    {{ latest.snr.toFixed(1) }} dB
                                </v-chip>
                            </div>
                            <v-progress-circular v-else size="16" width="2" indeterminate color="white" />
                        </div>

                        <div class="d-flex align-center flex-wrap mt-2" :style="{ gap: smAndDown ? '16px' : '28px' }">
                            <div class="d-flex align-center" style="gap: 12px;">
                                <v-icon :icon="héroCondition.icône" :size="smAndDown ? 44 : 56" />
                                <div>
                                    <div :class="smAndDown ? 'text-h3' : 'text-h2'" class="font-weight-bold" style="line-height: 1;">
                                        {{ latest.temp !== null ? latest.temp.toFixed(1) : '—' }}<span class="text-h5">°C</span>
                                    </div>
                                    <div class="texte-hero-secondaire text-body-2 mt-1">{{ héroCondition.texte }}</div>
                                </div>
                            </div>

                            <div class="d-flex flex-wrap" :style="{ gap: smAndDown ? '12px 20px' : '12px 32px' }">
                                <div class="stat-hero">
                                    <div class="texte-hero-secondaire text-caption">Humidité</div>
                                    <div class="text-subtitle-1 font-weight-bold">
                                        {{ latest.hum !== null ? `${latest.hum.toFixed(0)} %` : '—' }}
                                    </div>
                                </div>
                                <div class="stat-hero">
                                    <div class="texte-hero-secondaire text-caption">Vent</div>
                                    <div class="text-subtitle-1 font-weight-bold">
                                        {{ msVersKmh(latest.ventMs) !== null ? `${msVersKmh(latest.ventMs)!.toFixed(1)} km/h` : '—' }}
                                        <span v-if="latest.dirDeg !== null" class="texte-hero-secondaire text-caption">
                                            {{ cardinal(latest.dirDeg) }}
                                        </span>
                                    </div>
                                </div>
                                <div class="stat-hero">
                                    <div class="texte-hero-secondaire text-caption">Pluie actuelle</div>
                                    <div class="text-subtitle-1 font-weight-bold">
                                        {{ latest.tauxPrécip !== null ? `${latest.tauxPrécip.toFixed(1)} mm/h` : '—' }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="texte-hero-secondaire text-caption mt-3">
                            <v-icon icon="mdi-clock-outline" size="12" class="me-1" />
                            <template v-if="latest.ts">
                                Dernière donnée il y a {{ tempsDepuisDernière }} — {{ new Date(latest.ts).toLocaleString('fr-CA') }}
                            </template>
                            <template v-else>En attente de données…</template>
                        </div>
                    </v-card-text>
                </v-card>

                <!-- ── Précipitations ───────────────────────────────────────── -->
                <div class="d-flex align-center mt-5 mb-2" style="gap: 8px;">
                    <v-icon icon="mdi-weather-pouring" size="20" color="primary" />
                    <span class="text-subtitle-1 font-weight-bold">Précipitations</span>
                </div>

                <!-- Tuiles de cumuls -->
                <div class="grille-pluie">
                    <v-card v-for="tuile in tuilesPluie" :key="tuile.label" variant="tonal" color="primary" rounded="xl" class="tuile-pluie">
                        <v-card-text class="pa-3">
                            <div class="d-flex align-center text-caption" style="gap: 6px; opacity: 0.85;">
                                <v-icon :icon="tuile.icône" size="14" />
                                {{ tuile.label }}
                            </div>
                            <div class="text-h5 font-weight-bold mt-1" style="line-height: 1.1;">
                                <template v-if="(isLoadingPluie || isLoadingSaison) && tuile.valeur === null">
                                    <v-progress-circular size="18" width="2" indeterminate />
                                </template>
                                <template v-else>
                                    {{ tuile.valeur !== null ? tuile.valeur.toFixed(1) : '—' }}
                                    <span class="text-body-2" style="opacity: 0.7;">mm</span>
                                </template>
                            </div>
                        </v-card-text>
                    </v-card>
                </div>

                <!-- Graphique barres journalières + cumul -->
                <v-card variant="outlined" rounded="xl" class="mt-3">
                    <v-card-title class="py-2 px-4">
                        <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                            <div v-if="!smAndDown" class="d-flex align-center" style="gap: 8px; flex: 0 0 auto;">
                                <v-icon icon="mdi-chart-bar" size="18" />
                                <span class="text-subtitle-2">Historique de pluie</span>
                            </div>
                            <div class="d-flex align-center" style="gap: 4px;">
                                <v-btn
                                    size="x-small" rounded="pill"
                                    :variant="périodePluie === '7j' ? 'flat' : 'tonal'"
                                    :color="périodePluie === '7j' ? 'primary' : undefined"
                                    @click="appliquerPériodePluie('7j')"
                                >7 j</v-btn>
                                <v-btn
                                    size="x-small" rounded="pill"
                                    :variant="périodePluie === '30j' ? 'flat' : 'tonal'"
                                    :color="périodePluie === '30j' ? 'primary' : undefined"
                                    @click="appliquerPériodePluie('30j')"
                                >30 j</v-btn>
                                <v-btn
                                    size="x-small" rounded="pill"
                                    :variant="périodePluie === 'saison' ? 'flat' : 'tonal'"
                                    :color="périodePluie === 'saison' ? 'primary' : undefined"
                                    @click="appliquerPériodePluie('saison')"
                                >Saison</v-btn>
                            </div>
                            <v-date-input
                                v-model="plagePluie"
                                label="Période"
                                multiple="range"
                                variant="outlined"
                                density="compact"
                                rounded="lg"
                                hide-details
                                autocomplete="off"
                                prepend-icon=""
                                prepend-inner-icon="mdi-calendar-range-outline"
                                :picker-props="{ color: 'primary' }"
                                style="min-width: 190px; flex: 1 1 190px; max-width: 300px;"
                            />
                            <v-spacer v-if="!smAndDown" />
                            <v-btn
                                icon="mdi-refresh"
                                variant="text"
                                size="small"
                                :loading="isLoadingPluie"
                                @click="rafraîchirPluie"
                            />
                        </div>
                    </v-card-title>
                    <v-divider />
                    <v-card-text class="pa-2">
                        <div v-if="isLoadingPluie" class="d-flex align-center justify-center chart-zone">
                            <v-progress-circular indeterminate color="primary" />
                        </div>
                        <div
                            v-else-if="pluieAffichée.length === 0"
                            class="d-flex align-center justify-center text-medium-emphasis text-caption chart-zone"
                        >
                            Aucune donnée sur la période sélectionnée
                        </div>
                        <VChart
                            v-else
                            :option="pluieChartOption"
                            autoresize
                            class="chart-zone"
                            style="width: 100%;"
                        />
                    </v-card-text>
                </v-card>

                <!-- ── Autres mesures ───────────────────────────────────────── -->
                <div class="d-flex align-center mt-5 mb-2" style="gap: 8px;">
                    <v-icon icon="mdi-gauge" size="20" color="primary" />
                    <span class="text-subtitle-1 font-weight-bold">Mesures</span>
                    <span class="text-caption text-medium-emphasis">— touchez une tuile pour l'historique</span>
                </div>

                <v-row dense>
                    <v-col v-for="m in MÉTRIQUES" :key="m.id" cols="6" sm="4" md="3">
                        <v-card
                            variant="outlined"
                            rounded="xl"
                            class="tuile-metrique"
                            :class="{ 'tuile-active': métriqueSélectionnée === m.id }"
                            :style="métriqueSélectionnée === m.id ? { borderColor: m.couleur } : undefined"
                            @click="surClicTuile(m.id)"
                        >
                            <v-card-text class="pa-3 d-flex align-center" style="gap: 10px;">
                                <v-avatar size="34" rounded="lg" :style="{ background: `${m.couleur}22` }">
                                    <v-icon :icon="m.icône" size="18" :style="{ color: m.couleur }" />
                                </v-avatar>
                                <div style="min-width: 0;">
                                    <div class="text-caption text-medium-emphasis text-truncate">{{ m.label }}</div>
                                    <div class="text-subtitle-1 font-weight-bold" style="line-height: 1.1;">
                                        {{ valeurMétrique(m.id) !== null ? valeurMétrique(m.id)!.toFixed(m.décimales) : '—' }}
                                        <span class="text-caption text-medium-emphasis">{{ m.unité }}</span>
                                    </div>
                                    <div v-if="m.id === 'vent' && latest.dirDeg !== null" class="text-caption text-medium-emphasis">
                                        {{ cardinal(latest.dirDeg) }} ({{ latest.dirDeg.toFixed(0) }}°)
                                    </div>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Graphique de la mesure sélectionnée -->
                <v-expand-transition>
                    <v-card v-if="métriqueMeta" variant="outlined" rounded="xl" class="mt-3">
                        <v-card-title class="py-2 px-4">
                            <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                                <div class="d-flex align-center" style="gap: 8px;">
                                    <v-icon :icon="métriqueMeta.icône" size="18" :style="{ color: métriqueMeta.couleur }" />
                                    <span class="text-subtitle-2">{{ métriqueMeta.label }}</span>
                                </div>
                                <div class="d-flex align-center" style="gap: 4px;">
                                    <v-btn
                                        size="x-small" rounded="pill"
                                        :variant="métriquePériodeJours === 1 ? 'flat' : 'tonal'"
                                        :color="métriquePériodeJours === 1 ? 'primary' : undefined"
                                        @click="appliquerPériodeMétrique(1)"
                                    >24 h</v-btn>
                                    <v-btn
                                        size="x-small" rounded="pill"
                                        :variant="métriquePériodeJours === 7 ? 'flat' : 'tonal'"
                                        :color="métriquePériodeJours === 7 ? 'primary' : undefined"
                                        @click="appliquerPériodeMétrique(7)"
                                    >7 j</v-btn>
                                    <v-btn
                                        size="x-small" rounded="pill"
                                        :variant="métriquePériodeJours === 30 ? 'flat' : 'tonal'"
                                        :color="métriquePériodeJours === 30 ? 'primary' : undefined"
                                        @click="appliquerPériodeMétrique(30)"
                                    >30 j</v-btn>
                                </div>
                                <v-spacer />
                                <v-btn icon="mdi-close" variant="text" size="small" @click="métriqueSélectionnée = null" />
                            </div>
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pa-2">
                            <div v-if="isLoadingMétrique" class="d-flex align-center justify-center chart-zone">
                                <v-progress-circular indeterminate color="primary" />
                            </div>
                            <div
                                v-else-if="métriquePoints.length === 0"
                                class="d-flex align-center justify-center text-medium-emphasis text-caption chart-zone"
                            >
                                Aucune donnée sur la période sélectionnée
                            </div>
                            <VChart
                                v-else
                                :option="métriqueChartOption"
                                autoresize
                                class="chart-zone"
                                style="width: 100%;"
                            />
                        </v-card-text>
                    </v-card>
                </v-expand-transition>

            </template>
        </div>
    </v-card>

    <!-- Renommer une station -->
    <v-dialog v-model="renameDialog" max-width="480" :fullscreen="smAndDown">
        <v-card rounded="xl">
            <v-card-title class="d-flex align-center pt-4 px-5" style="gap: 10px;">
                <v-icon icon="mdi-pencil-outline" size="20" color="primary" />
                <span class="text-subtitle-1 font-weight-bold">Renommer la station</span>
                <v-spacer />
                <v-btn icon="mdi-close" variant="text" size="small" @click="renameDialog = false" />
            </v-card-title>
            <v-divider />
            <v-card-text class="pt-4">
                <v-text-field
                    v-model="renameLabel"
                    label="Nom affiché"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    autofocus
                    counter="255"
                    :hint="`Identifiant technique : ${selectedDevice?.name ?? ''}`"
                    persistent-hint
                    @keyup.enter="saveLabel"
                />
            </v-card-text>
            <v-card-actions class="px-5 pb-4">
                <v-spacer />
                <v-btn variant="text" rounded="pill" @click="renameDialog = false">Annuler</v-btn>
                <v-btn
                    color="primary" variant="flat" rounded="pill"
                    :loading="isSavingLabel"
                    :disabled="renameLabel.trim().length === 0"
                    @click="saveLabel"
                >
                    Enregistrer
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
    flex-shrink: 0;
}

/* Comme la page Carte : la page occupe tout le viewport, sans marge ni coins arrondis. */
.page-card {
    height: calc(100vh - var(--v-layout-top, 64px) - var(--v-layout-bottom, 0px));
    display: flex;
    flex-direction: column;
    border-radius: 0;
}

.detail-scroll {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 16px;
    min-height: 0;
}

@media (max-width: 600px) {
    .detail-scroll {
        padding: 12px;
    }
}

/* ── Puces de stations ───────────────────── */
.chips-stations {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

@media (max-width: 600px) {
    .chips-stations {
        flex-wrap: nowrap;
        overflow-x: auto;
        scrollbar-width: none;
        padding-bottom: 2px;
    }
    .chips-stations::-webkit-scrollbar {
        display: none;
    }
    .chips-stations .v-chip {
        flex: 0 0 auto;
    }
}

/* ── Héro ────────────────────────────────── */
.hero-card {
    color: #fff;
}

.texte-hero-secondaire {
    color: rgba(255, 255, 255, 0.78);
}

.chip-hero {
    background: rgba(255, 255, 255, 0.18);
    color: #fff;
}

.btn-hero {
    color: rgba(255, 255, 255, 0.85);
}

.stat-hero {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 80px;
}

/* ── Tuiles ──────────────────────────────── */
.tuile-metrique {
    cursor: pointer;
    transition: transform 0.12s, border-color 0.12s;
    height: 100%;
}
.tuile-metrique:hover {
    transform: translateY(-2px);
}
.tuile-active {
    border-width: 2px;
}

.tuile-pluie {
    height: 100%;
}

.grille-pluie {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 8px;
}

/* ── Graphiques ──────────────────────────── */
.chart-zone {
    height: 300px;
}

@media (max-width: 600px) {
    .chart-zone {
        height: 240px;
    }
}
</style>
